import React from 'react';
import { CheckCircleIcon } from '../common/Icons';

export interface TemplateCardTemplate {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  version: string;
  is_system: boolean;
  is_active: boolean;
  navigation: { groups: Array<{ items: unknown[] }> };
}

interface TemplateCardProps {
  template: TemplateCardTemplate;
  onSelect: (slug: string) => void;
  isSelected?: boolean;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onSelect, isSelected = false }) => {
  const groupCount = template.navigation?.groups?.length ?? 0;
  const itemCount = template.navigation?.groups?.reduce(
    (sum, group) => sum + (group.items?.length ?? 0),
    0
  ) ?? 0;

  return (
    <div
      data-testid={`template-card-${template.slug}`}
      onClick={() => onSelect(template.slug)}
      style={{
        padding: '1.25rem',
        background: isSelected
          ? 'rgba(124, 58, 237, 0.08)'
          : 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: isSelected
          ? '1px solid rgba(124, 58, 237, 0.5)'
          : '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '16px',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        opacity: template.is_active ? 1 : 0.6,
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          const card = e.currentTarget;
          card.style.background = 'rgba(255, 255, 255, 0.06)';
          card.style.border = '1px solid rgba(139, 92, 246, 0.3)';
          card.style.boxShadow = '0 8px 32px rgba(139, 92, 246, 0.15)';
          card.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          const card = e.currentTarget;
          card.style.background = 'rgba(255, 255, 255, 0.03)';
          card.style.border = '1px solid rgba(255, 255, 255, 0.08)';
          card.style.boxShadow = '0 4px 24px rgba(0, 0, 0, 0.1)';
          card.style.transform = 'translateY(0)';
        }
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <span style={{ fontWeight: 600, fontSize: '15px', flex: 1 }}>
          {template.name}
        </span>
        <span className="badge badge-secondary" style={{ fontSize: '0.65rem' }}>
          v{template.version}
        </span>
        {template.is_system && (
          <span className="badge badge-info" style={{ fontSize: '0.65rem' }}>
            System
          </span>
        )}
        {!template.is_active && (
          <span className="badge badge-warning" style={{ fontSize: '0.65rem' }}>
            Inactive
          </span>
        )}
        {isSelected && (
          <CheckCircleIcon size={16} style={{ color: 'var(--primary)' }} />
        )}
      </div>

      {/* Description */}
      <p style={{
        fontSize: '13px',
        color: 'var(--text-secondary)',
        margin: '0 0 12px',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        minHeight: '36px',
      }}>
        {template.description || 'No description'}
      </p>

      {/* Footer: counts */}
      <div style={{
        fontSize: '12px',
        color: 'var(--text-tertiary)',
        display: 'flex',
        gap: '8px',
      }}>
        <span>{groupCount} group{groupCount !== 1 ? 's' : ''}</span>
        <span style={{ opacity: 0.5 }}>&middot;</span>
        <span>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
      </div>
    </div>
  );
};

export default TemplateCard;
